import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

const GIS_SRC = 'https://accounts.google.com/gsi/client';
const CLIENT_ID: string = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '';

let scriptPromise: Promise<void> | undefined;

/** Load the Google Identity Services script once, shared across all callers. */
function loadGis(): Promise<void> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${GIS_SRC}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

/**
 * Warm the GIS script without rendering anything, so the sign-in button is
 * interactive by the time a guest opens the account menu.
 */
export function preloadGoogleIdentity(): void {
  if (!CLIENT_ID) return;
  loadGis().catch(() => {
    // The render path (useGoogleSignInButton) logs its own failure.
  });
}

/**
 * Render the official Google button into `containerRef` and hand the returned
 * ID token to `onCredential`. The credential is verified server-side — the
 * client only relays it. The container must stay fully visible: GIS renders
 * the button in an iframe with occlusion detection that silently drops clicks
 * while the button is hidden (opacity-0 overlays stopped working when Google
 * tightened this server-side, 2026-07-08). The button is sized to blanket the
 * menu row that hosts it (see SignInMenuItem). Returns `configured: false`
 * (and renders nothing) when VITE_GOOGLE_CLIENT_ID is unset, so the app
 * degrades gracefully.
 */
export function useGoogleSignInButton({
  containerRef,
  onCredential,
}: {
  containerRef: RefObject<HTMLDivElement | null>;
  onCredential: (credential: string) => void;
}): { configured: boolean } {
  // Keep the latest callback without re-initializing GIS on every render.
  const callbackRef = useRef(onCredential);
  callbackRef.current = onCredential;

  useEffect(() => {
    if (!CLIENT_ID) {
      if (import.meta.env.DEV) {
        console.warn('VITE_GOOGLE_CLIENT_ID is not set — Google sign-in is disabled.');
      }
      return;
    }
    let cancelled = false;
    loadGis()
      .then(() => {
        if (cancelled || !containerRef.current || !window.google) return;
        window.google.accounts.id.initialize({
          client_id: CLIENT_ID,
          callback: (response) => callbackRef.current(response.credential),
        });
        window.google.accounts.id.renderButton(containerRef.current, {
          type: 'standard',
          // filled_black sits closest to the dark menu card the button lives in.
          theme: 'filled_black',
          // Sized to cover the w-56 account-menu row (224px wide, 'large' is
          // 40px tall) so the whole row is Google's button.
          size: 'large',
          shape: 'rectangular',
          width: 224,
        });
      })
      .catch((error: unknown) => {
        console.error('Google sign-in failed to initialize', error);
      });
    return () => {
      cancelled = true;
    };
  }, [containerRef]);

  return { configured: Boolean(CLIENT_ID) };
}
